// receiptSender.js
// Pipeline compartido para generar el comprobante (canvas), subirlo a Storage,
// guardar la URL en Firestore y enviarlo por WhatsApp.
// Usado tanto por PaymentReceipt.js (cobro normal) como por AdminHome.jsx
// (edición de monto desde el Historial de Cobros).

import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import logo from "../assets/logo.png";

// ========== Helpers básicos ==========
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withRetry(requestFn, maxAttempts = 3, baseDelay = 2000) {
  let lastErr;
  for (let i = 1; i <= maxAttempts; i++) {
    try {
      return await requestFn();
    } catch (err) {
      lastErr = err;
      if (i === maxAttempts) break;
      const jitter = Math.floor(Math.random() * 500);
      await sleep(baseDelay * i + jitter);
    }
  }
  throw lastErr;
}

export function toWhatsAppJid(phone) {
  let num = String(phone).replace(/\D/g, "");
  if (num.length === 10) return "521" + num;
  if (num.length === 12 && num.startsWith("52") && num[2] !== "1")
    return "521" + num.slice(2);
  if (num.length === 13 && num.startsWith("521")) return num;
  return num;
}

export function formatDateMx(dateValue) {
  const date = dateValue?.seconds
    ? new Date(dateValue.seconds * 1000)
    : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX");
}

// ========== Generación de recibo con Canvas nativo ==========
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, x, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let y = 0;
  const lines = [];
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line.trim());
      line = word + " ";
    } else {
      line = test;
    }
  }
  if (line.trim()) lines.push(line.trim());
  lines.forEach((l, i) => {
    ctx.fillText(l, x, y + i * lineHeight);
  });
  return lines.length * lineHeight;
}

export async function generateReceiptCanvas(transaction) {
  const W = 400;
  const pad = 24;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Pre-calcular altura
  canvas.width = W;
  canvas.height = 800; // temporal, se recortará

  let y = pad;

  // Fondo blanco
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, 800);

  // Logo
  try {
    const logoImg = await loadImage(logo);
    const logoW = 80;
    const logoH = (logoImg.height / logoImg.width) * logoW;
    ctx.drawImage(logoImg, (W - logoW) / 2, y, logoW, logoH);
    y += logoH + 12;
  } catch {
    y += 12;
  }

  // Título
  const tipo = transaction.tipo;
  const title =
    tipo === "adeudo"
      ? "Adeudo Registrado"
      : tipo === "abono"
      ? "Abono Exitoso"
      : "Pago Exitoso";
  ctx.fillStyle = "#861E3D";
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, W / 2, y);
  y += 30;

  // Línea separadora
  ctx.strokeStyle = "#e0e0e0";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(W - pad, y);
  ctx.stroke();
  y += 16;

  // Helper para filas de datos
  ctx.textAlign = "left";
  const addRow = (label, value, opts = {}) => {
    const fontSize = opts.fontSize || 14;
    const color = opts.color || "#333333";
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = color;
    ctx.fillText(label, pad, y);
    const labelW = ctx.measureText(label).width;
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillText(` ${value}`, pad + labelW, y);
    y += fontSize + 8;
  };

  // Folio
  if (transaction.folio) {
    addRow("Folio:", transaction.folio);
  }

  // Campos según tipo
  if (tipo === "adeudo") {
    addRow("Cargo del día:", `$${Number(transaction.dayAmount || 0).toFixed(2)}`);
    if (transaction.totalDebt)
      addRow("Adeudo total:", `$${Number(transaction.totalDebt).toFixed(2)}`);
  } else if (tipo === "abono") {
    addRow("Monto Abonado:", `$${Number(transaction.totalAmount || 0).toFixed(2)}`, {
      fontSize: 16,
    });
  } else {
    addRow("Cargo del día:", `$${Number(transaction.dayAmount || 0).toFixed(2)}`);
    if (transaction.extraTotal > 0)
      addRow("Extras:", `$${Number(transaction.extraTotal).toFixed(2)}`);
    addRow("Total:", `$${Number(transaction.totalAmount || 0).toFixed(2)}`, {
      fontSize: 16,
    });
  }

  y += 4;

  // Agente y negocio
  ctx.font = "13px Arial, sans-serif";
  ctx.fillStyle = "#777777";
  const actionLabel =
    tipo === "adeudo"
      ? "Registrado por:"
      : tipo === "abono"
      ? "Abonado por:"
      : "Cobrado por:";
  ctx.fillText(`${actionLabel} ${transaction.agentName || ""}`, pad, y);
  y += 18;
  const targetLabel =
    tipo === "adeudo"
      ? "Negocio:"
      : tipo === "abono"
      ? "A cargo de:"
      : "Pagado por:";
  ctx.fillText(`${targetLabel} ${transaction.businessName || ""}`, pad, y);
  y += 18;

  // Fecha
  ctx.font = "11px Arial, sans-serif";
  ctx.fillStyle = "#aaaaaa";
  const dateVal = transaction.date?.seconds
    ? new Date(transaction.date.seconds * 1000)
    : new Date(transaction.date);
  ctx.fillText(dateVal.toLocaleString("es-MX"), pad, y);
  y += 20;

  // Texto legal
  ctx.font = "9px Arial, sans-serif";
  ctx.fillStyle = "#999999";
  ctx.textAlign = "center";
  const legalText =
    "Apartado B. Por el uso de la vía pública por comerciantes ambulantes o con puestos fijos y semifijos. Artículo 18.- Los derechos por el uso de la vía pública se causarán conforme a lo siguiente: I. Los comerciantes ambulantes, pagarán de $10.00 diarios; II. Los puestos fijos o semifijos pagarán de $10.00 pesos diarios por m².";
  y += wrapText(ctx, legalText, W / 2, W - pad * 2, 12);
  y += 6;

  ctx.font = "9px Arial, sans-serif";
  ctx.fillStyle = "#999999";
  ctx.fillText("*El presente comprobante no representa un permiso", W / 2, y);
  y += pad;

  // Recortar canvas a la altura real
  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = W;
  finalCanvas.height = y;
  const fCtx = finalCanvas.getContext("2d");
  fCtx.fillStyle = "#ffffff";
  fCtx.fillRect(0, 0, W, y);
  fCtx.drawImage(canvas, 0, 0);

  return new Promise((resolve) => finalCanvas.toBlob(resolve, "image/png"));
}

// ========== WhatsApp Cloud API ==========
export async function fetchWhatsAppConfig(db) {
  const configRef = doc(db, "config", "whatsapp");
  const configSnap = await getDoc(configRef);
  if (!configSnap.exists()) return null;
  const data = configSnap.data() || {};
  return {
    apiToken: data.wa_api_token || "",
    phoneId: data.wa_phone_id || "",
    templateName: data.wa_template_name || "",
    templateLang: data.wa_template_lang || "es_MX",
    sendMode: data.wa_send_mode || "qr",
  };
}

async function sendCloudTemplate({ config, phone, imageUrl, bodyParams }) {
  const payload = {
    messaging_product: "whatsapp",
    to: phone,
    type: "template",
    template: {
      name: config.templateName,
      language: { code: config.templateLang || "es_MX" },
      components: [
        {
          type: "body",
          parameters: bodyParams.map((value) => ({
            type: "text",
            text: String(value ?? ""),
          })),
        },
      ],
    },
  };

  if (imageUrl) {
    payload.template.components.unshift({
      type: "header",
      parameters: [{ type: "image", image: { link: imageUrl } }],
    });
  }

  const url = `https://graph.facebook.com/v21.0/${config.phoneId}/messages`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${config.apiToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errPayload = await response.json().catch(() => ({}));
    throw new Error(errPayload?.error?.message || "Error al enviar plantilla.");
  }
  return response.json().catch(() => ({}));
}

// Envía el recibo (ya generado) por WhatsApp. Lanza error si falla.
export async function sendWhatsAppReceipt({ db, transaction, receiptUrl }) {
  const phoneFormatted = toWhatsAppJid(transaction.businessPhone);
  if (!phoneFormatted || phoneFormatted.length < 12) {
    throw new Error("Teléfono del negocio inválido.");
  }
  const bodyParams = [
    transaction.ownerName || transaction.businessName || "",
    transaction.businessName || "",
    formatDateMx(transaction.date),
  ];
  const config = await fetchWhatsAppConfig(db);
  if (!config || !config.apiToken || !config.phoneId || !config.templateName) {
    throw new Error("Falta configurar WhatsApp API.");
  }
  if (config.sendMode === "qr") {
    throw new Error("El modo QR requiere backend.");
  }
  await withRetry(
    () =>
      sendCloudTemplate({
        config,
        phone: phoneFormatted,
        imageUrl: receiptUrl,
        bodyParams,
      }),
    3,
    2000
  );
}

// Pipeline completo: genera la imagen → la sube a Storage → guarda la URL en
// Firestore → la envía por WhatsApp. Devuelve la nueva receiptUrl.
// Callbacks opcionales: onStatus("generating"|"uploading"|"sending") y
// onReceiptUrl(url) que se invoca después de subir/guardar y antes de enviar
// el WhatsApp (para mostrar la imagen de inmediato).
export async function generateUploadAndSendReceipt({
  db,
  storage,
  transaction,
  onStatus,
  onReceiptUrl,
}) {
  onStatus?.("generating");
  const blob = await generateReceiptCanvas(transaction);

  onStatus?.("uploading");
  const collectionName = transaction.tipo === "adeudo" ? "adeudos" : "cobros";
  const urlField = transaction.tipo === "adeudo" ? "receiptURL" : "receiptUrl";
  const storagePath = `receipts/${transaction.id || Date.now()}.png`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: "image/png" });
  const receiptUrl = await getDownloadURL(storageRef);

  if (transaction.id) {
    await updateDoc(doc(db, collectionName, transaction.id), {
      [urlField]: receiptUrl,
    });
  }

  onReceiptUrl?.(receiptUrl);
  onStatus?.("sending");
  await sendWhatsAppReceipt({ db, transaction, receiptUrl });
  return receiptUrl;
}
